const PENDING_RETURN_TO_KEY = 'startus:auth:pending-return-to';

function getStorages() {
  if (typeof window === 'undefined') {
    return [];
  }

  return [window.sessionStorage, window.localStorage].filter(Boolean);
}

function readValue(key) {
  for (const storage of getStorages()) {
    try {
      const value = storage.getItem(key);
      if (value) {
        return value;
      }
    } catch {
      // Ignore storage read failures.
    }
  }

  return '';
}

function writeValue(key, value) {
  for (const storage of getStorages()) {
    try {
      storage.setItem(key, value);
    } catch {
      // Ignore storage write failures.
    }
  }
}

function removeValue(key) {
  for (const storage of getStorages()) {
    try {
      storage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures.
    }
  }
}

function isBlockedScheme(value) {
  return /^[a-zA-Z][a-zA-Z\d+.-]*:/i.test(value);
}

export function sanitizeReturnTo(input, fallback = '/mypage') {
  const value = String(input || '').trim();
  if (!value) {
    return fallback;
  }

  if (value.startsWith('//') || isBlockedScheme(value)) {
    return fallback;
  }

  if (value.startsWith('/')) {
    return value;
  }

  if (typeof window !== 'undefined') {
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        return fallback;
      }

      return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export function locationToReturnTo(locationLike, fallback = '') {
  if (!locationLike) {
    return fallback;
  }

  const pathname = locationLike.pathname || '/';
  const search = locationLike.search || '';
  const hash = locationLike.hash || '';
  return sanitizeReturnTo(`${pathname}${search}${hash}`, fallback);
}

export function getLocationStateReturnTo(location, fallback = '') {
  return locationToReturnTo(location?.state?.from, fallback);
}

export function getQueryReturnTo(search, fallback = '') {
  const params = new URLSearchParams(search || '');
  return sanitizeReturnTo(params.get('returnTo') || '', fallback);
}

export function savePendingReturnTo(candidate, fallback = '') {
  const sanitized = sanitizeReturnTo(candidate, fallback);
  if (!sanitized) {
    return '';
  }

  writeValue(PENDING_RETURN_TO_KEY, sanitized);
  return sanitized;
}

export function peekPendingReturnTo(fallback = '') {
  return sanitizeReturnTo(readValue(PENDING_RETURN_TO_KEY), fallback);
}

export function clearPendingReturnTo() {
  removeValue(PENDING_RETURN_TO_KEY);
}

export function consumePendingReturnTo(preferredReturnTo = '', fallback = '/mypage') {
  const preferred = sanitizeReturnTo(preferredReturnTo, '');
  const value = peekPendingReturnTo('');
  clearPendingReturnTo();
  return preferred || value || fallback;
}

export function buildLoginUrlWithReturnTo(returnTo = '/mypage') {
  const sanitized = sanitizeReturnTo(returnTo, '/mypage');
  return `/login?returnTo=${encodeURIComponent(sanitized)}`;
}

export function getPostLoginRedirectPath({
  oauthReturnTo = '',
  pendingFlowReturnTo = '',
  location = null,
  fallback = '/mypage',
} = {}) {
  const candidates = [
    sanitizeReturnTo(oauthReturnTo, ''),
    sanitizeReturnTo(pendingFlowReturnTo, ''),
    getLocationStateReturnTo(location, ''),
    getQueryReturnTo(location?.search, ''),
    peekPendingReturnTo(''),
    sanitizeReturnTo(fallback, '/mypage'),
  ];

  return candidates.find(Boolean) || '/mypage';
}

export function requireLoginOrContinue({
  isAuthenticated,
  location,
  navigate,
  fallback = '/mypage',
}) {
  if (isAuthenticated) {
    return true;
  }

  const returnTo = locationToReturnTo(location, fallback);
  savePendingReturnTo(returnTo);
  navigate(buildLoginUrlWithReturnTo(returnTo), {
    replace: true,
    state: { from: location },
  });
  return false;
}
